import { useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import { useIOLookups } from "@/hooks/histories/useIOLookups";
import { useIODetail, useIOUpdate, useIOBulkUpdate } from "@/hooks/histories/useIODetail";
import { useIOGroup } from "@/hooks/histories/useIOGroup";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";

import IOSummary from "./IOSummary";
import IOGroupSummary from "./IOGroupSummary";
import IOFields from "./IOFields";
import IORelations from "./IORelations";

import styles from "./io-form.module.scss";

const IOForm = ({ isOpen, historyId, outputCode, balanceId, listType, filters, onClose, onSuccess }) => {
	const location = useLocation();
	const action = new URLSearchParams(location.search).get("action");

	const isGroupMode = Boolean(outputCode) && Boolean(balanceId);

	const [formData, setFormData] = useState({});

	useEffect(() => {
		setFormData({});
	}, [historyId, outputCode, balanceId]);

	const { data: groupData, isLoading: groupLoading, error: groupError } = useIOGroup(outputCode, balanceId, { enabled: isOpen && isGroupMode });

	const groupIdsString = useMemo(() => groupData?.application_ids?.join(",") || "", [groupData]);

	const lookupsKey = isGroupMode ? groupIdsString : historyId;
	const {
		data: lookupsData,
		isLoading: lookupsLoading,
		error: lookupsError,
	} = useIOLookups(lookupsKey, {
		enabled: isOpen && (!isGroupMode || Boolean(groupData)),
	});

	const {
		data: applicationData,
		isLoading: applicationLoading,
		error: applicationError,
	} = useIODetail(historyId, { enabled: isOpen && !isGroupMode && Boolean(historyId) });

	const updateMutation = useIOUpdate(listType, filters);
	const bulkMutation = useIOBulkUpdate(listType, filters);

	const isLoading = isGroupMode ? groupLoading || lookupsLoading : lookupsLoading || applicationLoading;
	const error = isGroupMode ? groupError || lookupsError : lookupsError || applicationError;

	const lookupOptions = useMemo(() => {
		if (!lookupsData) return null;

		const ensureValidOptions = (options = []) =>
			options.map((opt) => ({
				value: Number(opt.value),
				label: String(opt.label),
				...opt,
			}));

		return {
			oas: ensureValidOptions(lookupsData.oas || []),
			estatesParents: ensureValidOptions(lookupsData.estates_parents || []),
			estates: ensureValidOptions(lookupsData.estates || []),
			categories: ensureValidOptions(lookupsData.categories || []),
			species: ensureValidOptions(lookupsData.species || []),
			varieties: ensureValidOptions(lookupsData.varieties || []),
			quarters: ensureValidOptions(lookupsData.quarters || []),
			businessCenters: ensureValidOptions(lookupsData.business_center || []),
			operators: ensureValidOptions(lookupsData.operators || []),
			hauliers: ensureValidOptions(lookupsData.hauliers || []),
		};
	}, [lookupsData]);

	useEffect(() => {
		if (!isGroupMode || !groupData) return;
		setFormData((prev) => ({
			...prev,
			oas: (groupData.ids_oas || []).map(Number),
		}));
	}, [groupData, isGroupMode]);

	useEffect(() => {
		if (isGroupMode) return;
		const application = applicationData?.io;
		if (application && lookupOptions && application.id) {
			const formatDateTimeLocal = (isoString) => {
				if (!isoString) return "";
				return isoString.slice(0, 16);
			};

			const parseIdsArray = (ids) => {
				if (!ids || !Array.isArray(ids)) return [];
				return ids.map(Number).filter((id) => !Number.isNaN(id));
			};

			setFormData({
				...application,
				date_weight: formatDateTimeLocal(application.date_weight),
				product_container_weight: application.product_container_weight || 0,
				product_density: application.product_density || 0,
				oas: parseIdsArray(application.ids_oas),
				estatesParents: parseIdsArray(application.ids_estates_parents),
				estates: parseIdsArray(application.ids_estates),
				categories: parseIdsArray(application.ids_categories),
				species: parseIdsArray(application.ids_species),
				varieties: parseIdsArray(application.ids_varieties),
				quarters: parseIdsArray(application.ids_quarters),
				businessCenters: parseIdsArray(application.ids_business_centers),
				operators: parseIdsArray(application.ids_operators),
				hauliers: parseIdsArray(application.ids_hauliers),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationData, lookupOptions]);

	const handleClose = () => {
		setFormData({});
		onClose();
	};

	const handleSaveSingle = async () => {
		if (formData.empty_container && formData.not_used) {
			toast.error("No es posible guardar la aplicación como envase vacío y no utilizado.");
			return;
		}

		const applicationPayload = {
			id: Number(historyId),
			entry: formData.entry || 0,
			date_weight: formData.date_weight,
			weight: formData.weight,
			empty_container: formData.empty_container || false,
			not_used: formData.not_used || false,
			output_code: formData.output_code || "",
			application_order_code: "",
			ids_oas: formData.oas || [],
			manual_weight: formData.manual_weight,
			manual_unit: formData.manual_unit,
			product_container_weight: formData.product_container_weight,
			product_density: formData.product_density,
			ids_estates_parents: formData.estatesParents || [],
			ids_estates: formData.estates || [],
			ids_categories: formData.categories || [],
			ids_species: formData.species || [],
			ids_varieties: formData.varieties || [],
			ids_quarters: formData.quarters || [],
			ids_business_centers: formData.businessCenters || [],
			ids_operators: formData.operators || [],
			ids_hauliers: formData.hauliers || [],
			ids_warehouses: [],
		};

		try {
			await updateMutation.mutateAsync({ historyId, application: applicationPayload });
			toast.success("Aplicación actualizada correctamente.");
			handleClose();
			if (onSuccess) onSuccess();
		} catch (err) {
			toast.error(err?.response?.data?.message || err?.response?.data?.error || "Error al actualizar la aplicación");
		}
	};

	const handleSaveGroup = async () => {
		if (!groupData?.application_ids?.length) return;

		try {
			await bulkMutation.mutateAsync({
				ids: groupData.application_ids,
				idsOAs: formData.oas || [],
			});
			toast.success("OAs asignadas correctamente.");
			handleClose();
			if (onSuccess) onSuccess();
		} catch (err) {
			toast.error(err?.response?.data?.message || err?.response?.data?.error || "Error al asignar OAs");
		}
	};

	const updateField = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	useEffect(() => {
		if (error) {
			toast.error(error.message || "Error al cargar la aplicación");
			handleClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	if (!isOpen) return null;

	if (isLoading || !lookupOptions) {
		return (
			<CustomModalForm open={true} onClose={handleClose} showSave={false}>
				<CustomFetching center size="lg" />
			</CustomModalForm>
		);
	}

	if (isGroupMode) {
		if (!groupData) return null;

		return (
			<CustomModalForm
				open={true}
				title="Asignar Ordenes de Aplicación"
				summary={<IOGroupSummary group={groupData} />}
				onClose={handleClose}
				onSubmit={handleSaveGroup}
				textForm="Asignar"
				isLoading={bulkMutation.isLoading}
				processingMessage="Actualizando órdenes de aplicación..."
			>
				<div className={styles.form}>
					<div className={styles.section}>
						<div className={styles.fieldsGrid}>
							<CustomInputSelect
								label="Ordenes de Aplicación"
								name="oas"
								options={lookupOptions.oas}
								value={formData.oas || []}
								onChange={(val) => updateField("oas", val)}
								placeholder="Seleccionar OA..."
								isClearable={true}
								isMulti={true}
								col={12}
							/>
						</div>
					</div>
				</div>
			</CustomModalForm>
		);
	}

	if (!applicationData?.io) return null;

	return (
		<CustomModalForm
			open={true}
			title="Editar Registro"
			summary={<IOSummary application={applicationData.io} />}
			onClose={handleClose}
			onSubmit={handleSaveSingle}
			textForm="Guardar"
			isLoading={updateMutation.isLoading}
			processingMessage="Actualizando aplicación de IO..."
		>
			<div className={styles.form}>
				<IOFields formData={formData} application={applicationData.io} oaOptions={lookupOptions.oas} onUpdateField={updateField} />

				{action === "editar" && <IORelations formData={formData} lookupOptions={lookupOptions} onUpdateField={updateField} />}
			</div>
		</CustomModalForm>
	);
};

export default IOForm;
